import * as React from 'react';

import { CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export function DatePicker({
  value,
  onChange,
  className,
  placeholder,
  container,
  ...props
}: {
  value: Date | undefined;
  onChange: (value: Date | undefined) => void;
  className?: string;
  placeholder?: string;
  /** Portal target for the calendar, e.g. a dialog raised above the default z-index. */
  container?: React.ComponentProps<typeof PopoverContent>['container'];
} & Omit<React.ComponentPropsWithoutRef<typeof Button>, 'value' | 'onChange'>) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            className={cn(
              'hover:border-ring bg-background h-11 w-full justify-start rounded-md text-base font-normal md:text-sm',
              className,
            )}
            {...props}
          >
            {value
              ? value.toLocaleDateString()
              : (placeholder ?? t('common.selectDate'))}

            <CalendarIcon className="ml-auto" />
          </Button>
        }
      />
      <PopoverContent
        className="w-auto overflow-hidden p-0"
        align="start"
        container={container}
      >
        <Calendar
          mode="single"
          selected={value}
          defaultMonth={value}
          captionLayout="dropdown"
          onSelect={(date) => {
            onChange(date);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
